import React from 'react';
import { motion } from 'framer-motion';
import { Globe, MessageCircle, Database, Calendar } from 'lucide-react';
const chipVariants = {
  hidden: {
    opacity: 0,
    scale: 0.94
  },
  show: {
    opacity: 1,
    scale: 1
  }
};
interface ToolChipProps {
  label: string;
  icon: React.ComponentType<{
    size?: number;
    strokeWidth?: number;
  }>;
  delay: number;
}
function ToolChip({
  label,
  icon: Icon,
  delay
}: ToolChipProps) {
  return <motion.div variants={chipVariants} initial="hidden" whileInView="show" viewport={{
    once: true,
    amount: 0.4
  }} transition={{
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1],
    delay
  }} className="relative z-10 flex items-center gap-2 bg-arena border border-carbon/10 rounded-[12px] px-3.5 py-2.5 shadow-card whitespace-nowrap" data-id="element-221" data-mp="1k10gah6op8tb">
      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-verde/10 text-verde flex-shrink-0" data-id="element-222" data-mp="22z39z9a6ftxg">
        <Icon size={14} strokeWidth={2} data-id="element-223" data-mp="27q1p45jfilz4" />
      </span>
      <span className="font-body text-[13px] font-medium text-carbon" data-id="element-224" data-mp="27rmp4aoiix4d">
        {label}
      </span>
    </motion.div>;
}
export function Problem() {
  return <section className="bg-arena py-[56px] md:py-[80px]" data-id="element-225" data-mp="13ri7id0bls15">
      <div className="max-w-[1200px] mx-auto px-6 md:px-20" data-id="element-226" data-mp="1hhu1yikdo2vg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-227" data-mp="1hj54yg6016li">
          {/* Left: copy */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-228" data-mp="1cpu3yvnvbk1i">
            <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-widest mb-6" data-id="element-229" data-mp="1wd0958v7m7qd">
              EL PROBLEMA
            </span>
            <h2 className="font-display text-[32px] md:text-[44px] font-bold text-carbon leading-[1.1] mb-6 max-w-[480px]" data-id="element-230" data-mp="2251c8rumwon">
              Tu empresa ya tiene herramientas. El problema es que no
              trabajan juntas.
            </h2>
            <p className="font-body text-[16px] text-carbon/60 leading-[1.7] max-w-[440px] mb-6" data-id="element-231" data-mp="2670xbgorxpwv">
              Una web genera interés. WhatsApp recibe mensajes. El CRM
              guarda contactos. El calendario organiza citas. Pero alguien
              termina conectando todo manualmente.
            </p>
            <p className="font-display text-[20px] font-bold text-verde" data-id="element-232" data-mp="2k2v4nv9wg77v">
              ORIGEN convierte herramientas aisladas en un sistema.
            </p>
          </motion.div>

          {/* Right: tools connecting to ORIGEN in a symmetric cross */}
          <div className="relative w-full max-w-[380px] mx-auto grid grid-cols-3 grid-rows-3 gap-3 md:gap-4 h-[280px] md:h-[300px]" data-id="element-233" data-mp="2ysajccw5om77">
            {/* Connecting lines — centered through the middle row/column */}
            <motion.div initial={{
            opacity: 0,
            scaleY: 0
          }} whileInView={{
            opacity: 1,
            scaleY: 1
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.15
          }} className="absolute left-1/2 top-0 bottom-0 w-[1.5px] -translate-x-1/2 origin-center" style={{
            backgroundImage: 'repeating-linear-gradient(to bottom, rgba(33,77,67,0.42) 0, rgba(33,77,67,0.42) 4px, transparent 4px, transparent 8px)'
          }} aria-hidden="true" data-id="element-234" data-mp="37ution0qggop" />
            <motion.div initial={{
            opacity: 0,
            scaleX: 0
          }} whileInView={{
            opacity: 1,
            scaleX: 1
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.15
          }} className="absolute top-1/2 left-0 right-0 h-[1.5px] -translate-y-1/2 origin-center" style={{
            backgroundImage: 'repeating-linear-gradient(to right, rgba(33,77,67,0.42) 0, rgba(33,77,67,0.42) 4px, transparent 4px, transparent 8px)'
          }} aria-hidden="true" data-id="element-235" data-mp="1axxn64983t3x" />

            {/* Row 1 */}
            <div data-id="element-236" data-mp="14f17z7nc5afz" />
            <div className="flex items-center justify-center" data-id="element-237" data-mp="14fjeqy7mj725">
              <ToolChip label="Web" icon={Globe} delay={0.2} data-id="element-238" data-mp="19dksr4q70g6r" />
            </div>
            <div data-id="element-239" data-mp="19fp05wnavxbs" />

            {/* Row 2 */}
            <div className="flex items-center justify-center" data-id="element-240" data-mp="1x2x347x9auan">
              <ToolChip label="WhatsApp" icon={MessageCircle} delay={0.28} data-id="element-241" data-mp="222a03lhudhqy" />
            </div>
            <div className="flex items-center justify-center" data-id="element-242" data-mp="2242vu5qx2hk6">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true,
              amount: 0.4
            }} transition={{
              duration: 0.5,
              ease: 'easeOut',
              delay: 0.45
            }} className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-verde text-arena font-display font-bold text-[13px] shadow-cta flex-shrink-0" data-id="element-243" data-mp="1ncucgedlxsfh">
                ORIGEN
              </motion.div>
            </div>
            <div className="flex items-center justify-center" data-id="element-244" data-mp="4cl8tjz714tr">
              <ToolChip label="Calendario" icon={Calendar} delay={0.28} data-id="element-245" data-mp="i4suogoxvbdo" />
            </div>

            {/* Row 3 */}
            <div data-id="element-246" data-mp="e5ajwdqu0gok" />
            <div className="flex items-center justify-center" data-id="element-247" data-mp="e6v3x9fish86">
              <ToolChip label="CRM" icon={Database} delay={0.2} data-id="element-248" data-mp="3ml3sm6ex8nv2" />
            </div>
            <div data-id="element-249" data-mp="3hm99t4gyezay" />
          </div>
        </div>
      </div>
    </section>;
}